import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import {
  AlertCircle,
  ClipboardCopy,
  History,
  Loader2,
  Pencil,
  RefreshCw,
  Save,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import VoiceoverPanel from "./VoiceoverPanel";
import PersonaPicker, { PersonaBadge } from "./PersonaPicker";

const PERSONA_LS_KEY = "eda.persona";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const SOURCE_BADGE = {
  ai_generated: { cls: "bg-violet-50 text-violet-700", label: "AI generated" },
  ai_regenerated: { cls: "bg-violet-50 text-violet-700", label: "AI regenerated" },
  human_edited: { cls: "bg-sky-50 text-sky-700", label: "Human edited" },
};

/**
 * Props:
 *   - runId
 *   - runStatus  (only "completed" runs can generate)
 */
export default function NarrationPanel({ runId, runStatus }) {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canGenerate = hasPermission("workflow.run");
  const canEdit = hasPermission("workflow.run"); // presenter+ per Phase 1b matrix
  const canRegenerate = hasPermission("workflow.write"); // builder+

  const [selectedScriptId, setSelectedScriptId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [editSummary, setEditSummary] = useState("");
  // R2-Phase-3 M4.2: persona for next generate; persisted per user.
  const [persona, setPersonaState] = useState(() => {
    if (typeof window === "undefined") return null;
    const v = window.localStorage.getItem(PERSONA_LS_KEY);
    return v && v !== "default" ? v : null;
  });
  const setPersona = (next) => {
    setPersonaState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PERSONA_LS_KEY, next || "default");
    }
  };

  // Personas manifest — used here to render the PersonaBadge label next to
  // a version that was generated for a specific persona.
  const personasQuery = useQuery({
    queryKey: ["personas"],
    queryFn: async () => (await api.get("/personas")).data,
    staleTime: 5 * 60_000,
  });
  const personaItems = personasQuery.data?.items || [];

  const versions = useQuery({
    queryKey: ["narration-versions", runId],
    queryFn: async () =>
      (await api.get(`/demo-runs/${runId}/narration/versions`)).data,
  });

  // The currently-displayed script: either an explicitly-picked version, or
  // the latest (which is /narration).
  const latestScriptId = (versions.data && versions.data[0]?.script_id) || null;
  const effectiveScriptId = selectedScriptId ?? latestScriptId;

  const script = useQuery({
    queryKey: ["narration-script", effectiveScriptId],
    queryFn: async () =>
      effectiveScriptId
        ? (await api.get(`/narration-scripts/${effectiveScriptId}`)).data
        : null,
    enabled: !!effectiveScriptId,
  });

  const generate = useMutation({
    mutationFn: async (regenerate) =>
      (
        await api.post(`/demo-runs/${runId}/generate-narration`, {
          regenerate: !!regenerate,
          persona: persona || null,
        })
      ).data,
    onSuccess: (s) => {
      toast.success(
        s.source === "ai_regenerated"
          ? `Regenerated as v${s.version}`
          : `Generated v${s.version}`,
      );
      setSelectedScriptId(null);
      queryClient.invalidateQueries({ queryKey: ["narration-versions", runId] });
      queryClient.invalidateQueries({ queryKey: ["demo-run", runId] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const saveEdit = useMutation({
    mutationFn: async () =>
      (
        await api.put(`/demo-runs/${runId}/narration`, {
          body_markdown: draft,
          edit_summary: editSummary || null,
        })
      ).data,
    onSuccess: (s) => {
      toast.success(`Saved as v${s.version}`);
      setEditing(false);
      setSelectedScriptId(null);
      queryClient.invalidateQueries({ queryKey: ["narration-versions", runId] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  useEffect(() => {
    if (editing && script.data) {
      setDraft(script.data.body_markdown || "");
      setEditSummary("");
    }
  }, [editing, script.data]);

  const hasAnyVersion = (versions.data || []).length > 0;

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="narration-panel"
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
          <Sparkles size={16} className="text-violet-600" />
          Narration
          {script.data && (
            <span className="ml-1 font-mono text-[10px] font-normal text-slate-500">
              v{script.data.version} · {script.data.word_count} words
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {hasAnyVersion && versions.data.length > 1 && (
            <Select
              value={effectiveScriptId || ""}
              onValueChange={(v) =>
                setSelectedScriptId(v === latestScriptId ? null : v)
              }
            >
              <SelectTrigger
                className="h-8 w-[170px] text-xs"
                data-testid="narration-version-select"
              >
                <SelectValue placeholder="Pick version" />
              </SelectTrigger>
              <SelectContent>
                {(versions.data || []).map((v) => {
                  const meta = SOURCE_BADGE[v.source] || SOURCE_BADGE.ai_generated;
                  return (
                    <SelectItem
                      key={v.script_id}
                      value={v.script_id}
                      data-testid={`narration-version-v${v.version}`}
                    >
                      v{v.version} · {meta.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
          {script.data && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(script.data.body_markdown);
                toast.success("Copied to clipboard");
              }}
              data-testid="narration-copy"
            >
              <ClipboardCopy size={13} className="mr-1" />
              Copy
            </Button>
          )}
        </div>
      </header>

      {!hasAnyVersion ? (
        <div className="space-y-3 py-4 text-center">
          {runStatus !== "completed" ? (
            <p
              className="flex items-center justify-center gap-2 text-sm text-slate-500"
              data-testid="narration-needs-completed"
            >
              <AlertCircle size={14} />
              Narration is available once the run completes successfully.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-500">
                No narration has been generated for this run yet.
              </p>
              <div className="max-w-sm">
                <PersonaPicker
                  value={persona}
                  onChange={setPersona}
                  disabled={generate.isPending}
                />
              </div>
              <Button
                onClick={() => generate.mutate(false)}
                disabled={!canGenerate || generate.isPending}
                className="gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300"
                data-testid="narration-generate-button"
              >
                {generate.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                Generate Narration
              </Button>
              {!canGenerate && (
                <p className="text-xs text-slate-400">
                  Needs presenter role.
                </p>
              )}
            </>
          )}
        </div>
      ) : !script.data ? (
        <div className="py-6 text-center text-sm text-slate-500">
          <Loader2 size={14} className="mx-auto mb-2 animate-spin" />
          Loading version…
        </div>
      ) : editing ? (
        <div className="space-y-3" data-testid="narration-editor">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={18}
            className="font-mono text-xs"
            data-testid="narration-editor-textarea"
          />
          <input
            type="text"
            placeholder="Optional: short edit summary (e.g. 'shortened close')"
            value={editSummary}
            onChange={(e) => setEditSummary(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-xs focus:border-indigo-400 focus:outline-none"
            data-testid="narration-editor-summary"
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
              data-testid="narration-editor-cancel"
            >
              <X size={13} className="mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => saveEdit.mutate()}
              disabled={saveEdit.isPending || !draft.trim()}
              className="gap-1 bg-indigo-600 hover:bg-indigo-700"
              data-testid="narration-editor-save"
            >
              {saveEdit.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Save size={13} />
              )}
              Save as new version
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span
              className={`rounded-full px-2 py-0.5 font-medium ${(SOURCE_BADGE[script.data.source] || SOURCE_BADGE.ai_generated).cls}`}
              data-testid="narration-source-badge"
            >
              {(SOURCE_BADGE[script.data.source] || SOURCE_BADGE.ai_generated).label}
            </span>
            {script.data.persona && (
              <PersonaBadge personaKey={script.data.persona} items={personaItems} />
            )}
            <span>by {script.data.created_by}</span>
            <span>·</span>
            <span>{formatDate(script.data.created_at)}</span>
            {script.data.latency_ms != null && (
              <>
                <span>·</span>
                <span>
                  {script.data.model} ·{" "}
                  {Math.round(script.data.latency_ms / 100) / 10}s ·{" "}
                  ~{script.data.input_tokens}+{script.data.output_tokens} tok
                </span>
              </>
            )}
            {script.data.edit_summary && (
              <>
                <span>·</span>
                <span className="italic">"{script.data.edit_summary}"</span>
              </>
            )}
          </div>

          <article
            className="prose prose-sm prose-slate max-w-none rounded-lg border border-slate-100 bg-slate-50/40 p-4
                       prose-headings:mb-2 prose-headings:mt-4 prose-headings:text-slate-900
                       prose-h2:text-base prose-h2:font-semibold
                       prose-p:text-slate-700 prose-strong:text-slate-900"
            data-testid="narration-rendered"
          >
            <ReactMarkdown>{script.data.body_markdown}</ReactMarkdown>
          </article>

          <NarrationCostEstimate narrationId={script.data.script_id} />

          <VoiceoverPanel runId={runId} narrationReady={true} />

          {canRegenerate && (
            <div
              className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:flex-row sm:items-end"
              data-testid="narration-regen-block"
            >
              <div className="flex-1">
                <PersonaPicker
                  value={persona}
                  onChange={setPersona}
                  disabled={generate.isPending}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const ask = persona
                    ? `Regenerate this narration for the ${personaItems.find((p) => p.key === persona)?.label || persona} persona? A new version will be created.`
                    : "Regenerate will call the LLM again and create a new version. Continue?";
                  if (window.confirm(ask)) {
                    generate.mutate(true);
                  }
                }}
                disabled={generate.isPending}
                data-testid="narration-regenerate"
                className="gap-1 self-end"
              >
                {generate.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <RefreshCw size={13} />
                )}
                Regenerate{persona ? ` as ${personaItems.find((p) => p.key === persona)?.label || persona}` : ""}
              </Button>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            {canEdit && (
              <Button
                size="sm"
                onClick={() => setEditing(true)}
                className="gap-1 bg-indigo-600 hover:bg-indigo-700"
                data-testid="narration-edit"
              >
                <Pencil size={13} />
                Edit
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function NarrationCostEstimate({ narrationId }) {
  // R3 — quote-sheet pills.  Cheapest provider rendered emerald.
  const q = useQuery({
    queryKey: ["narration-cost-estimate", narrationId],
    queryFn: async () =>
      (await api.get(`/narrations/${narrationId}/cost-estimate`)).data,
    enabled: !!narrationId,
    staleTime: 60_000,
  });
  if (q.isLoading || q.isError || !q.data || !q.data.quotes?.length) {
    return null;
  }
  const { quotes, cheapest_provider, character_count } = q.data;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50/60 p-3"
      data-testid="narration-cost-estimate"
    >
      <div className="mb-1.5 flex items-baseline justify-between text-[11px] text-slate-500">
        <span>Estimated voiceover cost:</span>
        <span className="font-mono">
          {character_count.toLocaleString()} chars
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {quotes.map((q2) => {
          const isCheapest = q2.provider === cheapest_provider;
          return (
            <span
              key={q2.provider}
              className={
                isCheapest
                  ? "inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800"
                  : "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600"
              }
              data-testid={`narration-cost-quote-${q2.provider}`}
              data-cheapest={isCheapest ? "true" : "false"}
            >
              <span className="capitalize">{q2.provider}</span>
              <span className="font-mono">{`$${q2.estimated_cost_usd.toFixed(4)}`}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
